import { API, Auth } from 'aws-amplify';
import { getClientMetadataHeaders } from './client-metadata';
import { OPERATION_MAPPINGS } from './operation-mappings';

// Bind to preserve Amplify API context (original method relies on `this`)
const originalGraphQL = (API as any).graphql.bind(API);

function extractOperationName(query: string): string {
  const match = query?.toString()?.match(/(?:query|mutation|subscription)\s+(\w+)/);
  return match ? match[1] : '';
}

async function getCognitoJWT(): Promise<string> {
  const session = await Auth.currentSession();
  return session.getIdToken().getJwtToken();
}

// Intercept API.graphql and route selected operations to REST
(API as any).graphql = async (options: any) => {
  const operationName = extractOperationName(options?.query);
  const mapping = OPERATION_MAPPINGS[operationName];

  if (!mapping) {
    return originalGraphQL(options);
  }

  try {
    const url = mapping.urlBuilder
      ? mapping.urlBuilder(options?.variables || {})
      : mapping.restEndpoint;

    const requestOptions: RequestInit = {
      method: mapping.method,
      headers: {
        ...getClientMetadataHeaders(),
        Authorization: `Bearer ${await getCognitoJWT()}`,
        'Content-Type': 'application/json'
      }
    };

    if (mapping.method !== 'GET' && options?.variables) {
      requestOptions.body = JSON.stringify(options.variables);
    }

    // Prefer explicit env; otherwise infer backend base with localhost fallback
    const explicitBase = process.env.REACT_APP_API_BASE_URL || '';
    const isLocal = !explicitBase && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    const base = explicitBase || `http://${window.location.hostname}:3001`;

    // If not local and URL starts with /api, drop the prefix to hit root-mounted routes in cloud
    const path = !isLocal && url.startsWith('/api') ? url.replace(/^\/api/, '') : url;

    const fullUrl = `${base}${path}`;

    const response = await fetch(fullUrl, requestOptions);
    if (!response.ok) {
      if (response.status === 404 && typeof mapping.handleNotFound === 'function') {
        return mapping.handleNotFound(options?.variables);
      }
      let bodyDetail = '';
      try {
        const text = await response.clone().text().catch(() => '');
        // eslint-disable-next-line no-console
        console.error('[NET][REST][http_error]', { op: operationName, url: fullUrl, status: response.status, bodyPreview: text.slice(0, 400) });
        if (response.status >= 500 && text) {
          try {
            const parsed = JSON.parse(text);
            // Prefer specific error/message over generic labels
            const err = parsed?.error;
            const msg = parsed?.message;
            if (msg && msg !== err && err === "Lambda function error") {
              bodyDetail = `: ${msg}`;
            } else if (err) {
              bodyDetail = `: ${err}`;
            }
          } catch {
            bodyDetail = text.length < 200 ? `: ${text}` : '';
          }
        }
      } catch {}
      throw new Error(`HTTP ${response.status}: ${response.statusText}${bodyDetail}`);
    }
    const restData = await response.json();
    const transformed = mapping.transformer(restData, options?.variables);
    return transformed;
  } catch (err) {
    // We have a mapping, so we intentionally use REST. Don't fall back to GraphQL.
    throw err;
  }
};

export { extractOperationName };
